import React from 'react';
import { useVisibleTicker } from '../useVisibleTicker';

/* 各产品品牌色（原为 #F59E0B）统一收到强调色：
   五个演示在同一页滚动出现，各用自己的色会让强调色失去指示作用。
   SVG 的 stroke/fill 不吃 CSS 变量的回退链，这里取字面值。 */
const BRAND = '#ff6308';

const LEADERS = [
  { rank: 1, name: '张*明', team: '猎豹队', score: 2480 },
  { rank: 2, name: '李*华', team: '雄鹰队', score: 2310 },
  { rank: 3, name: '王*强', team: '猎豹队', score: 2150 },
  { rank: 4, name: '刘*婷', team: '磐石队', score: 1980 },
];

/* 金银铜是排名的语义色，保留层级差；只把第一名对齐强调色 */
const MEDAL = ['#ff6308', '#B8BCC4', '#A9743F'];
const MAX = LEADERS[0].score;

/**
 * 排名小程序 · 榜单模拟（微信小程序形态）。
 *
 * 刻意保留小程序特有的右上角胶囊控件——它是这个产品交付形态的身份标识，
 * 少了它就变成了普通 App，用户无法一眼判断"这是微信生态里的东西"。
 *
 * 条形宽度按积分比例真实换算，而不是随手给的装饰长度。
 */
const RankingDemo: React.FC = () => {
  const { ref, tick } = useVisibleTicker(3200);
  const tab = tick % 2;

  return (
    <div ref={ref} className="flex justify-center">
      <div className="sx-device sx-device-phone w-[248px] shrink-0">
        <div
          className="relative rounded-[22px] overflow-hidden flex flex-col"
          style={{ background: '#0b0b0c', aspectRatio: '232 / 400' }}
        >
          {/* 小程序顶部栏 + 胶囊控件 */}
          <div className="flex items-center justify-between px-3.5 pt-3 pb-2.5 shrink-0">
            <span className="text-[11px] font-medium text-white">谁是黑马</span>
            <span
              className="flex items-center gap-1.5 rounded-full px-2 py-0.5"
              style={{ border: '1px solid var(--sx-line-strong)' }}
              aria-hidden="true"
            >
              <span className="text-[9px] leading-none" style={{ color: 'var(--sx-mute)' }}>
                •••
              </span>
              <span
                className="w-2 h-2 rounded-full"
                style={{ border: '1px solid var(--sx-mute)' }}
              />
            </span>
          </div>

          {/* 榜单切换 */}
          <div className="flex gap-1 px-3 pb-2.5 shrink-0">
            {['个人榜', '队伍榜'].map((t, i) => (
              <span
                key={t}
                className="flex-1 text-center text-[10px] py-1.5 rounded-md transition-colors duration-500"
                style={{
                  background: i === tab ? 'rgba(245,158,11,0.12)' : 'transparent',
                  border: `1px solid ${i === tab ? 'rgba(245,158,11,0.3)' : 'var(--sx-line)'}`,
                  color: i === tab ? BRAND : 'var(--sx-mute)',
                }}
              >
                {t}
              </span>
            ))}
          </div>

          {/* 排名列表 */}
          <div className="flex-1 px-3 space-y-1.5 overflow-hidden">
            {LEADERS.map((p, i) => (
              <div
                key={p.rank}
                className="rounded-[10px] px-2.5 py-2"
                style={{
                  background: i < 3 ? 'var(--sx-surface-2)' : 'transparent',
                  border: '1px solid var(--sx-line)',
                }}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <span
                    className="sx-num text-[10px] w-3.5 text-center shrink-0"
                    style={{ color: i < 3 ? MEDAL[i] : 'var(--sx-mute)' }}
                  >
                    {p.rank}
                  </span>
                  <span className="text-[10px] text-white truncate flex-1">
                    {tab === 0 ? p.name : p.team}
                  </span>
                  <span className="sx-num text-[10px] shrink-0" style={{ color: BRAND }}>
                    {p.score}
                  </span>
                </div>
                {/* 积分条：宽度 = 该积分占榜首的比例 */}
                <div className="h-[3px] rounded-full overflow-hidden" style={{ background: '#1a1c20' }}>
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${(p.score / MAX) * 100}%`,
                      background: i < 3 ? BRAND : '#3a3f48',
                      transition: 'width 1.1s cubic-bezier(0.22,1,0.36,1)',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* 我的位次 + 兑奖入口 */}
          <div className="px-3 pb-3 pt-2 shrink-0" style={{ borderTop: '1px solid var(--sx-line)' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="sx-mono text-[8px] mb-0.5">我的位次</p>
                <p className="text-[10px] text-white">
                  第 12 名 · <span className="sx-num">1680</span> 分
                </p>
              </div>
              <span
                className="text-[9px] rounded-full px-2.5 py-1"
                style={{ background: BRAND, color: '#231603', fontWeight: 500 }}
              >
                积分兑奖
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RankingDemo;
